import {Catalog, FormattedDate} from '../../../../common/types/common';
import {CatalogDropdownState, FormFieldProps} from '../../../../common/types/components';
import {GroupForm, GroupListState} from './reducers';
import {
    BankAccountFile,
    BankAccountSummaryState,
    CardCommissionAccountSummaryState, CommissionActFile,
    CommissionBankAccountFile, CommissionCardBackupCodeFile,
    CommissionIdentificationFile, CommissionIdentificationGroupFile
} from './types';
import {FileState} from '../../../loan/common/types/types';

export interface GroupSearchStateProps {
    groupTypes: Catalog[];
    marketNames: Catalog[];
    showMarketNames: Boolean;
    externalAdvisers: Catalog[];
    showExternalAdvisers: Boolean;
    searchQuery: string;
    groupTypeSelected: Catalog;
    marketNameSelected?: Catalog;
    externalAdviserSelected?: Catalog;
}

export interface GroupSearchDispatchProps {
    onChangeSearchQuery: (searchQuery: string) => void;
    onChangeGroupType: (group: Catalog) => void;
    onChangeMarketName: (marketName: Catalog) => void;
    onChangeExternalAdviser: (externalAdviser: Catalog) => void;
}

export interface GroupListStateProps {
    groups: GroupListState;
    groupList: GroupForm[];
    hasGroups: boolean;
    hasMoreGroup: boolean;
    lastPage: number;
    onLoad?: Function;
}

export interface GroupListDispatchProps {
    onLoad: (lastPage: number) => () => void;
    onPressGroup: (group: GroupForm) => () => void;
}

export interface GroupNewDispatchProps {
    onPressAddGroup: () => void;
}

export interface GroupFormStateProps {
    nameField: FormFieldProps<string>;
    ptxIdField: FormFieldProps<string>;
    addressField: FormFieldProps<string>;
    motorfyIdField: FormFieldProps<string>;
    createdAt: string;
    supervisors: Catalog[];
    supervisorSelected: Catalog;
    advisers: Catalog[];
    adviserSelected: Catalog;
    adviserField: FormFieldProps<Catalog>;
    marketNames: Catalog[];
    marketName: Catalog;
    marketNameField: FormFieldProps<Catalog>;
    externalAdvisers: Catalog[];
    externalAdviser: Catalog;
    externalAdviserField: FormFieldProps<Catalog>;
    postalCodeField: FormFieldProps<string>;
    whatsappNumberField: FormFieldProps<string>;
    stateField: FormFieldProps<string>;
    cityField: FormFieldProps<string>;
    municipalityField: FormFieldProps<string>;
    colonies: CatalogDropdownState[];
    colonyField: FormFieldProps<Catalog>;
    groupType: Catalog[];
    inventoryList: Catalog[];
    inventory: Catalog;
    groupTypeSelected: Catalog;
    groupTypeField: FormFieldProps<Catalog>;
    users: Catalog[];
    groupList: Catalog[];
    groupListSelected: Catalog;
    groupListField: FormFieldProps<Catalog>;
    warrantyStatusField: FormFieldProps<boolean>;
    paymentType: Catalog[];
    paymentTypeSelected: Catalog;
    paymentTypeField: FormFieldProps<Catalog>;
    billingMailField: FormFieldProps<string>;
    creditStatusField: FormFieldProps<boolean>;
    insuranceStatusField: FormFieldProps<boolean>;
    quoteStatusField: FormFieldProps<boolean>;
    premiumInventoryStatusField: FormFieldProps<boolean>;
    biometricsEnabledField: FormFieldProps<boolean>;
    expressProfilerStatusField: FormFieldProps<boolean>;
    approvalsStatusField: FormFieldProps<boolean>;
    motorfyStatusField: FormFieldProps<boolean>;
    coppelStatusField: FormFieldProps<boolean>;
    creditsNumber: number;
    guaranteesNumber: number;
    quotationsNumber: number;
    insuranceNumber: number;
    action: string;
    hasNoFieldsError: boolean;
    onPressMigrate?: () => void;
    countrySelected: string;
    showFundingInfo: boolean;
    isUserGroupAdmin: boolean;
    bankAccountsInfo: BankAccountSummaryState[];
    cardCommissionAccountsInfo: CardCommissionAccountSummaryState[];
    syncForcemanager: boolean;
    syncForcemanagerError: string;
    calculatorPremiumInventoryEnabledField: FormFieldProps<Catalog>;
    calculatorsPremiumInventory: Catalog[];
}

export interface GroupFormDispatchProps {
    onPressClose: () => void;
    onChangeName: (name: string, error: string) => void;
    onChangePtxId: (ptxId: string, error: string) => void;
    onChangeAddress: (address: string, error: string) => void;
    onChangeSupervisor: (supervisor: Catalog) => void;
    onChangeAdviser: (adviser: Catalog) => void;
    onChangeMarketName: (marketName: Catalog, error: string) => void;
    onChangeExternalAdviser: (externalAdviser: Catalog, error: string) => void;
    onChangePostalCode: Function;
    onChangewhatsappNumber: Function;
    onChangeState: (state: string, error: string) => void;
    onChangeCity: (city: string, error: string) => void;
    onChangeMunicipality: (municipality: string, error: string) => void;
    onChangeColony: (colony: string, error: string) => void;
    onChangeGroupType: (groupType: Catalog) => void;
    onChangeInventory: (inventory: Catalog) => void;
    onChangeGroupList: (groupList: Catalog) => void;
    onChangeWarrantyStatus: (warrantyStatus: boolean, error: string) => void;
    onChangePaymentType: (paymentType: Catalog, error: string) => void;
    onChangeBillingMail: (billingMail: string, error: string) => void;
    onChangeCreditStatus: (creditStatus: boolean, error: string) => void;
    onChangeInsuranceStatus: (insuranceStatus: boolean, error: string) => void;
    onChangeQuoteStatus: (quoteStatus: boolean, error: string) => void;
    onChangePremiumInventoryStatus: (premiumInventoryStatus: boolean, error: string) => void;
    onChangeBiometricsEnabled: (biometricsEnabled: boolean, error: string) => void;
    onChangeExpressProfilerStatus: (expressProfilerStatus: boolean, error: string) => void;
    onChangeApprovalsStatus: (approvalsStatus: boolean, error: string) => void;
    onChangeCalculatorPremiumInventoryEnabled: (calculatorPremiumInventoryEnabled: Catalog, error: string) => void;
    onChangeMotorfyStatus: (status: boolean, error: string) => void;
    onChangeCoppelStatus: (status: boolean, error: string) => void;
    onPressSaveNewGroup: () => void;
    onPressMigrate: (groupListSelected: Catalog) => () => void;
    onPressDelete: () => void;
    onPressUser: (id: string) => void;
    onPressCreateAccount: () => void;
    onPressCreateCardCommissionAccount: () => void;
    onPressAccountDetail: (item: BankAccountSummaryState) => void;
    onPressCardCommissionAccountDetail: (item: CardCommissionAccountSummaryState) => void;
    onClickSyncForcemanager: () => void;
    onPressDeleteUserFromGroup: Function;
}

export interface GroupMigrationStateProps {
    numberRequests: number;
    originGroup: Catalog;
    targetGroup: Catalog;
    migrationResult: number;
    isSuccess: boolean;
}

export interface GroupMigrationDispatchProps {
    onPressMigrationConfirm: () => void;
    onPressClose: () => void;
}

export interface GroupDeleteStateProps {
    group: Catalog;
    isSuccess: boolean;
    cardCommissionDeleteReasons?: Catalog[];
}

export interface CardCommissionDeleteStateProps {
    group: Catalog;
    commissionCardId: string;
}

export interface GroupDeleteDispatchProps {
    onPressDeleteConfirm: () => void;
    onPressClose: () => void;
}

export interface CardCommissionDeleteDispatchProps {
    onPressDeleteConfirm: (commissionCardId: string) => (deleteReason: Catalog, deleteReasonOther: string) => void;
    onPressClose: () => void;
}

export enum BankAccountFormType {
    CREATE_FORM_FROM_LOAN = 'CREATE_FORM_FROM_LOAN',
    CREATE_FORM_FROM_GROUP = 'CREATE_FORM_FROM_GROUP'
}

export enum CardCommissionAccountFormType {
    CREATE_FORM_FROM_LOAN = 'CREATE_FORM_FROM_LOAN',
    CREATE_FORM_FROM_GROUP = 'CREATE_FORM_FROM_GROUP'
}

export interface BankAccountFormStateProps {
    banksList: Catalog[]
    bankSelected: FormFieldProps<Catalog>
    accountNumber: FormFieldProps<string>
    beneficiaryName: FormFieldProps<string>
    clabe: FormFieldProps<string>
    dealerAccountStatusFile: FormFieldProps<BankAccountFile>
    dealerOfficialIdentificationFile: FormFieldProps<BankAccountFile>
    owner: Catalog
    createAt: FormattedDate
    formType: BankAccountFormType
    groupId: string
    target: string
}

export interface CardCommissionAccountFormStateProps {
    cardNumber: FormFieldProps<string>
    beneficiaryName: FormFieldProps<string>
    owner: Catalog
    createAt: FormattedDate
    formType: CardCommissionAccountFormType
    groupId: string
    actFile: FormFieldProps<CommissionActFile>
    identificationFile: FormFieldProps<CommissionIdentificationFile>
    identificationGroupFile: FormFieldProps<CommissionIdentificationGroupFile>
    cardBackupCodeFile: FormFieldProps<CommissionCardBackupCodeFile>
}

export interface CommissionBankAccountFormStateProps {
    banksList: Catalog[]
    bankSelected: FormFieldProps<Catalog>
    accountNumber: FormFieldProps<string>
    beneficiaryName: FormFieldProps<string>
    clabe: FormFieldProps<string>
    dealerAccountStatusFile: FormFieldProps<CommissionBankAccountFile>
    dealerOfficialIdentificationFile: FormFieldProps<CommissionBankAccountFile>
    createAt: FormattedDate
    loadExistCommissionBankAccount: boolean
}

export interface BankAccountFormDispatchProps {
    onCancel: Function
    onSave: Function
    onChangeBank: (field: Catalog, error: string) => void
    onChangeAccountNumber: (field: string, error: string) => void
    onChangeBeneficiary: (field: string, error: string) => void
    onChangeClabe: (field: string, error: string) => void
    onDropFileAccount: (field: BankAccountFile, error: string) => void
    onDropFileIdentification: (field: BankAccountFile, error: string) => void
    onDeleteFile: Function
}

export interface CardCommissionAccountFormDispatchProps {
    onCancel: Function
    onSave: Function
    onChangeAccountNumber: (field: string, error: string) => void
    onChangeBeneficiary: (field: string, error: string) => void
    onDropFileAct: (field: CommissionActFile, error: string) => void
    onDropFileIdentification: (field: CommissionIdentificationFile, error: string) => void
    onDropFileIdentificationGroup: (field: CommissionIdentificationGroupFile, error: string) => void
    onDropFileCardBackupCode: (field: CommissionCardBackupCodeFile, error: string) => void
    onDeleteFile: Function
}

export interface CommissionBankAccountFormDispatchProps {
    onCancel: Function
    onSave: Function
    onChangeBank: (field: Catalog, error: string) => void
    onChangeAccountNumber: (field: string, error: string) => void
    onChangeBeneficiary: (field: string, error: string) => void
    onChangeClabe: (field: string, error: string) => void
    onDropFileAccount: (field: CommissionBankAccountFile, error: string) => void
    onDropFileIdentification: (field: CommissionBankAccountFile, error: string) => void
    onDeleteFile: Function
}

export interface BankAccountDetailsProps {
    bankAccountId: string;
    accountNumber: string;
    bank: string;
    beneficiaryName: string;
    clabe: string;
    dealerAccountStatusFile: FileState;
    dealerOfficialIdentificationFile: FileState;
    ownerName: string;
    ownerId: string;
    createdAt: FormattedDate;
    isUserGroupAdmin: boolean;
}

export interface CardCommissionAccountDetailsProps {
    commissionCardId: string;
    cardNumber: string;
    cardHolderName: string;
    hideButtonDelete: boolean;
    actFile: FileState;
    identificationFile: FileState;
    identificationGroupFile: FileState;
    cardBackupCodeFile: FileState;
}

export interface BankAccountDetailsDispatchProps {
    onCancel: Function
}

export interface CardCommissionAccountDetailsDispatchProps {
    onCancel: Function
    onDelete: Function
}
